import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The camera, done once, for both the baseline photo and the identity check.
 *
 * Written after an iPhone 6 showed a pitch-black frame and then failed to take
 * the picture at all. Two separate faults, both of which this fixes and both of
 * which are easy to reintroduce:
 *
 *  1. SAFARI WILL NOT START A STREAM WITHOUT play().
 *     Setting `srcObject` is enough on Android and on desktop Chrome, so the
 *     bug is invisible unless you test on an iPhone. Safari attaches the stream
 *     and renders nothing until play() is called, and play() returns a promise
 *     that rejects silently if it is ignored.
 *
 *  2. THE FLASH MUST NOT UNMOUNT THE VIDEO.
 *     The old code rendered the white flash by returning a different element,
 *     which tore the <video> out of the DOM. The capture then drew from a
 *     detached element — a black square, or an exception. The flash is now an
 *     overlay painted OVER a video that never stops playing.
 *
 * Everything else here exists because this runs on old phones in dark car
 * parks: the shutter stays disabled until the camera has genuinely produced a
 * frame, the resolution request degrades rather than failing, and a frame that
 * comes out black is refused locally instead of being uploaded and reviewed by
 * a person who cannot see anything either.
 */

interface Props {
  /** Shown above the viewfinder. */
  hint?: string;
  shutterLabel?: string;
  onCapture: (blob: Blob) => void;
  onUnavailable: (message: string) => void;
}

/** Below this mean brightness the frame is dark enough to be useless. */
const MIN_BRIGHTNESS = 18;

export function SelfieCamera({ hint, shutterLabel = 'Take photo',
                              onCapture, onUnavailable }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [flash, setFlash] = useState(false);
  const [busy, setBusy] = useState(false);
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [note, setNote] = useState<string | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const open = async () => {
      setReady(false);
      stop();

      /*
       * Three attempts, each less demanding than the last. An iPhone 6 front
       * camera cannot deliver 960x960, and while `ideal` is meant to degrade
       * gracefully, older Safari sometimes fails the whole request instead.
       * Asking for less is better than not opening at all.
       */
      const attempts: MediaStreamConstraints[] = [
        { video: { facingMode: facing, width: { ideal: 960 }, height: { ideal: 960 } },
          audio: false },
        { video: { facingMode: facing }, audio: false },
        { video: true, audio: false },
      ];

      let stream: MediaStream | null = null;
      for (const c of attempts) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(c);
          break;
        } catch {
          /* try the next, simpler request */
        }
      }

      if (cancelled) { stream?.getTracks().forEach((t) => t.stop()); return; }
      if (!stream) {
        onUnavailable('The camera would not open. Check that this app is allowed '
          + 'to use the camera in your phone settings, then try again.');
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;

      video.srcObject = stream;
      // THE LINE THAT MATTERED. Without it Safari shows black forever.
      try {
        await video.play();
      } catch {
        onUnavailable('The camera opened but the picture would not start. Close '
          + 'the app completely and open it again.');
        return;
      }
    };

    void open();
    return () => { cancelled = true; stop(); };
  }, [facing, stop, onUnavailable]);

  // Only a real frame counts as ready — videoWidth stays 0 until one arrives,
  // and capturing before that is what produced the black square.
  const onLoaded = () => {
    const v = videoRef.current;
    if (v && v.videoWidth > 0) setReady(true);
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video || busy) return;

    if (video.videoWidth === 0) {
      setNote('The camera has not started yet. Wait a moment and try again.');
      return;
    }

    setBusy(true);
    setNote(null);

    // Overlay, not a screen swap: the video keeps playing underneath.
    setFlash(true);
    await new Promise((r) => setTimeout(r, 280));

    const size = Math.min(video.videoWidth, video.videoHeight);
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) { setFlash(false); setBusy(false); return; }

    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    if (facing === 'user') {
      // Mirror back, so the saved photo is not a mirror image of the person.
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    setFlash(false);

    /*
     * Refuse a black frame here rather than uploading it. An all-dark photo is
     * worthless to the face comparison AND to the person reviewing it, and the
     * driver is the only one who can fix it — they are standing right there.
     */
    let brightness = 255;
    try {
      const s = 32;
      const probe = document.createElement('canvas');
      probe.width = s; probe.height = s;
      const pctx = probe.getContext('2d');
      if (pctx) {
        pctx.drawImage(canvas, 0, 0, s, s);
        const { data } = pctx.getImageData(0, 0, s, s);
        let total = 0;
        for (let i = 0; i < data.length; i += 4) {
          total += (data[i]! * 0.299) + (data[i + 1]! * 0.587) + (data[i + 2]! * 0.114);
        }
        brightness = total / (data.length / 4);
      }
    } catch {
      /* If the probe fails, do not block the photo over it. */
    }

    if (brightness < MIN_BRIGHTNESS) {
      setNote('That came out too dark to use. Move somewhere brighter, or turn '
        + 'on a light, and take it again.');
      setBusy(false);
      return;
    }

    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/jpeg', 0.9));
    setBusy(false);
    if (!blob) {
      setNote('The photo could not be saved. Try once more.');
      return;
    }
    stop();
    onCapture(blob);
  };

  return (
    <>
      {hint && <p className="hint">{hint}</p>}

      <div className="id-frame">
        <video
          ref={videoRef}
          onLoadedMetadata={onLoaded}
          onCanPlay={onLoaded}
          autoPlay
          playsInline
          muted
          className="id-video"
          style={facing === 'user' ? { transform: 'scaleX(-1)' } : undefined}
        />
        {flash && <div className="id-flash-overlay" aria-hidden="true" />}
        {!ready && <div className="id-waiting">Starting the camera…</div>}
      </div>

      {note && <div className="banner warn-banner">{note}</div>}

      <button className="btn primary big" disabled={!ready || busy}
              onClick={() => void capture()}>
        {busy ? 'Working…' : ready ? shutterLabel : 'Starting the camera…'}
      </button>

      {/*
        * The owner's iPhone had no way out of a black front camera. Switching
        * to the back camera both proves the camera works at all and gives a
        * driver whose front lens is broken or covered a way to finish.
        */}
      <button className="btn link"
              onClick={() => setFacing(facing === 'user' ? 'environment' : 'user')}>
        {facing === 'user' ? 'Use the other camera' : 'Use the front camera'}
      </button>
    </>
  );
}
